import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { POSTS, findPost } from '@/content/posts';
import Markdown from '@/components/Markdown';
import { safeJsonLd } from '@/lib/jsonLd';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';

const SITE = (process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com').replace(/\/$/, '');

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Go Convert Documents`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', post.description);
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.setAttribute('content', post.keywords);

    return () => {
      document.title = 'Go Convert Documents — Free Online Document Converter';
      if (desc) desc.setAttribute('content', 'Convert any file format to PDF, Word, Excel, PowerPoint, and more. Fast, secure, no registration required.');
    };
  }, [post]);

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-3xl">Article not found</p>
        <Link to="/blog" className="brutal-btn mt-6 inline-block" data-testid="blog-back-link">
          ← Back to blog
        </Link>
      </div>
    );
  }

  const otherPosts = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    author: { '@type': 'Organization', name: post.author },
    publisher: {
      '@type': 'Organization',
      name: 'Go Convert Documents',
      logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` },
    },
    datePublished: post.date,
    keywords: post.keywords,
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold hover:underline underline-offset-4" data-testid="blog-back-link">
        <ArrowLeft weight="bold" size={16} /> All articles
      </Link>

      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500 mt-8">
        {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} · {post.author}
      </p>
      <h1
        data-testid="blog-post-title"
        className="font-display font-bold text-4xl md:text-5xl leading-tight text-slate-950 mt-2"
      >
        {post.title}
      </h1>
      <p className="text-zinc-700 mt-4 text-lg leading-relaxed">
        {post.description}
      </p>

      <article className="mt-10">
        <Markdown>{post.body}</Markdown>
      </article>

      {post.ctaTool && (
        <div className="mt-12 border-2 border-black bg-yellow-100 p-6 brutal-shadow">
          <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-600">
            Try it now
          </p>
          <p className="font-display font-semibold text-2xl mt-2 text-slate-950">
            Ready to try it yourself?
          </p>
          <Link
            data-testid="blog-cta"
            to={`/tools/${post.ctaTool}`}
            className="brutal-btn mt-4 inline-flex items-center gap-2"
          >
            Open the tool <ArrowRight weight="bold" size={18} />
          </Link>
        </div>
      )}

      {otherPosts.length > 0 && (
        <section className="mt-16 border-t-2 border-black pt-10">
          <h2 className="font-display font-bold text-2xl md:text-3xl">Keep reading</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {otherPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`related-post-${p.slug}`}
                className="border-2 border-black bg-white p-4 brutal-shadow-sm hover:brutal-shadow"
                style={{ transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
              >
                <p className="text-[10px] font-mono-plex uppercase tracking-widest text-zinc-500">
                  {new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </p>
                <p className="font-semibold mt-1 text-sm leading-tight">{p.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }}
      />
    </div>
  );
}
